// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * A row action shown as an icon (design doc §10.5).
 *
 * Every list shows the **same actions in the same place on every row**; one that does not
 * apply to a row is greyed out rather than left out. A button that appears on some rows and
 * not others makes the column shift under the pointer and leaves the reader wondering
 * whether the action exists at all — a disabled one says it exists, and hovering it says
 * why it is unavailable here ("3 archives were filed against it").
 *
 * `label` is the accessible name and the tooltip, so tests and screen readers find the
 * button by the same word the text button used to show. `disabledReason` becomes the
 * tooltip (and accessible description) when disabled; `hint` adds to it when enabled.
 *
 * The tooltip sits on a wrapper as well as on the button, because a disabled button
 * receives no pointer events in some browsers — the hover has to land on something that
 * does. `pointer-events: none` on the disabled button (styles.css) makes it land on the
 * wrapper consistently.
 */

import type { ReactNode } from "react";

interface IconButtonProps {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  /** Why this action is unavailable on this row. Shown on hover while disabled. */
  disabledReason?: string;
  /** What the action also does, when the label alone would hide it. Shown on hover. */
  hint?: string;
  danger?: boolean;
}

export function IconButton({
  label,
  icon,
  onClick,
  disabled = false,
  disabledReason,
  hint,
  danger = false,
}: IconButtonProps) {
  const tooltip = disabled && disabledReason ? disabledReason : hint ? `${label} — ${hint}` : label;
  return (
    <span className="icon-action tooltip-trigger" data-tooltip={tooltip}>
      <button
        type="button"
        className={danger ? "icon-button icon-button-danger" : "icon-button"}
        aria-label={label}
        disabled={disabled}
        onClick={onClick}
      >
        {icon}
      </button>
    </span>
  );
}
